import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Phone, Building2, Loader2, ArrowLeft } from 'lucide-react';
import { useClient } from '../../contexts/ClientContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const ClientAuth = () => {
  const navigate = useNavigate();
  const { login, register } = useClient();
  const { language } = useLanguage();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    company: ''
  });

  const translations = {
    en: {
      title: 'Client Portal',
      subtitle: 'Access your orders and project status',
      loginTab: 'Sign In',
      registerTab: 'Create Account',
      email: 'Email Address',
      password: 'Password',
      name: 'Full Name',
      phone: 'Phone Number',
      company: 'Company Name',
      loginBtn: 'Sign In',
      registerBtn: 'Create Account',
      noAccount: "Don't have an account?",
      hasAccount: 'Already have an account?',
      backToSite: 'Back to Website'
    },
    es: {
      title: 'Portal del Cliente',
      subtitle: 'Accede a tus pedidos y estado del proyecto',
      loginTab: 'Iniciar Sesión',
      registerTab: 'Crear Cuenta',
      email: 'Correo Electrónico',
      password: 'Contraseña',
      name: 'Nombre Completo',
      phone: 'Número de Teléfono',
      company: 'Nombre de la Empresa',
      loginBtn: 'Iniciar Sesión',
      registerBtn: 'Crear Cuenta',
      noAccount: '¿No tienes cuenta?',
      hasAccount: '¿Ya tienes cuenta?',
      backToSite: 'Volver al Sitio'
    },
    de: {
      title: 'Kundenportal',
      subtitle: 'Zugang zu Ihren Bestellungen und Projektstatus',
      loginTab: 'Anmelden',
      registerTab: 'Konto erstellen',
      email: 'E-Mail-Adresse',
      password: 'Passwort',
      name: 'Vollständiger Name',
      phone: 'Telefonnummer',
      company: 'Firmenname',
      loginBtn: 'Anmelden',
      registerBtn: 'Konto erstellen',
      noAccount: 'Noch kein Konto?',
      hasAccount: 'Bereits ein Konto?',
      backToSite: 'Zurück zur Website'
    }
  };

  const t = translations[language] || translations.es;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success(language === 'es' ? '¡Bienvenido!' : language === 'de' ? 'Willkommen!' : 'Welcome!');
      } else {
        if (!formData.name) {
          toast.error(language === 'es' ? 'El nombre es requerido' : 'Name is required');
          setLoading(false);
          return;
        }
        await register({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          phone: formData.phone || null,
          company: formData.company || null
        });
        toast.success(language === 'es' ? '¡Cuenta creada!' : language === 'de' ? 'Konto erstellt!' : 'Account created!');
      }
      navigate('/my-account');
    } catch (error) {
      const message = error.response?.data?.detail || (isLogin ? 'Login failed' : 'Registration failed');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#141619] flex flex-col">
      {/* Header */}
      <header className="p-4">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[#9CA3AF] hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">{t.backToSite}</span>
        </Link>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Logo/Title */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-[#D4AF37] to-[#F4C542] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-[#141619]" />
            </div>
            <h1 className="text-2xl font-bold text-[#D4AF37]">{t.title}</h1>
            <p className="text-[#9CA3AF] mt-2">{t.subtitle}</p>
          </div>

          {/* Form Card */}
          <div className="bg-[#1a1d21] rounded-xl border border-[#2a2d32] p-6">
            {/* Tabs */}
            <div className="flex mb-6 bg-[#141619] rounded-lg p-1">
              <button
                onClick={() => setIsLogin(true)}
                className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all ${
                  isLogin
                    ? 'bg-[#D4AF37] text-[#141619]'
                    : 'text-[#9CA3AF] hover:text-white'
                }`}
              >
                {t.loginTab}
              </button>
              <button
                onClick={() => setIsLogin(false)}
                className={`flex-1 py-2.5 text-sm font-medium rounded-md transition-all ${
                  !isLogin
                    ? 'bg-[#D4AF37] text-[#141619]'
                    : 'text-[#9CA3AF] hover:text-white'
                }`}
              >
                {t.registerTab}
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-[#9CA3AF] mb-2">
                      {t.name} *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                      <Input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="pl-10 bg-[#141619] border-[#2a2d32] text-white"
                        required={!isLogin}
                        data-testid="client-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#9CA3AF] mb-2">
                      {t.phone}
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="pl-10 bg-[#141619] border-[#2a2d32] text-white"
                        data-testid="client-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#9CA3AF] mb-2">
                      {t.company}
                    </label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                      <Input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                        className="pl-10 bg-[#141619] border-[#2a2d32] text-white"
                        data-testid="client-company-input"
                      />
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-[#9CA3AF] mb-2">
                  {t.email} *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="pl-10 bg-[#141619] border-[#2a2d32] text-white"
                    required
                    data-testid="client-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#9CA3AF] mb-2">
                  {t.password} *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    className="pl-10 bg-[#141619] border-[#2a2d32] text-white"
                    required
                    data-testid="client-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full py-6 bg-[#D4AF37] hover:bg-[#c9a432] text-[#141619] font-bold rounded-full"
                data-testid="client-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                ) : (
                  isLogin ? t.loginBtn : t.registerBtn
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientAuth;
